import { toRecord } from '../shared/network-tool-card-utils';
import { unwrapMcpToolOutput } from '../shared/unwrap-mcp-tool-output';

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const LABEL_FIELDS = [
  'title',
  'name',
  'hostname',
  'domain',
  'fqdn',
  'product',
  'softwareName',
  'url',
  'path',
] as const;
const BADGE_FIELDS = [
  'severity',
  'status',
  'scanType',
  'currentStage',
] as const;

export type McpQueryResultRow = {
  label: string;
  badge?: string;
};

export type McpQueryResult = {
  total: number;
  rows: McpQueryResultRow[];
};

export function coerceMcpOutputRecord(
  output: unknown,
): Record<string, unknown> | null {
  return toRecord(unwrapMcpToolOutput(output)) ?? null;
}

function readResultItems(
  record: Record<string, unknown>,
): Record<string, unknown>[] {
  const items = record.items ?? record.data ?? record.results ?? record.signals;

  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => toRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null);
}

function isDisplayableText(value: string): boolean {
  const trimmed = value.trim();

  return trimmed.length > 0 && !OBJECT_ID.test(trimmed);
}

function readDisplayString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];

  if (typeof value !== 'string' || !isDisplayableText(value)) {
    return undefined;
  }

  return value.trim();
}

function formatScanLabel(record: Record<string, unknown>): string | undefined {
  const scanType = readDisplayString(record, 'scanType');
  const status = readDisplayString(record, 'status');
  const stage = readDisplayString(record, 'currentStage');

  if (!scanType && !status) {
    return undefined;
  }

  return [scanType, status, stage].filter(Boolean).join(' · ');
}

function formatServiceLabel(
  record: Record<string, unknown>,
): string | undefined {
  const port = record.port;
  const protocol =
    readDisplayString(record, 'applicationProtocol') ??
    readDisplayString(record, 'transportProtocol');

  if (typeof port !== 'number') {
    return undefined;
  }

  return protocol ? `${protocol} ${port}` : `Port ${port}`;
}

function pickRowLabel(record: Record<string, unknown>): string | undefined {
  const named = LABEL_FIELDS.map((field) =>
    readDisplayString(record, field),
  ).find(Boolean);

  if (named) {
    return named;
  }

  return formatScanLabel(record) ?? formatServiceLabel(record);
}

function pickRowBadge(
  record: Record<string, unknown>,
  label: string,
): string | undefined {
  return BADGE_FIELDS.map((field) => readDisplayString(record, field)).find(
    (value) => value && value !== label && !label.includes(value),
  );
}

export function parseMcpQueryResult(output: unknown): McpQueryResult | null {
  const record = coerceMcpOutputRecord(output);

  if (!record) {
    return null;
  }

  const items = readResultItems(record);
  const rows = items
    .map((item) => {
      const label = pickRowLabel(item);

      if (!label) {
        return undefined;
      }

      const badge = pickRowBadge(item, label);

      return badge ? { label, badge } : { label };
    })
    .filter((row): row is McpQueryResultRow => row !== undefined);

  const total =
    typeof record.total === 'number'
      ? record.total
      : typeof record.count === 'number'
        ? record.count
        : rows.length > 0
          ? rows.length
          : undefined;

  if (total === undefined && rows.length === 0) {
    return null;
  }

  return {
    total: total ?? rows.length,
    rows,
  };
}
